import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

// Actions
import {
  addAltToPortfolioById,
  addFundToPortfolioById,
  addFundsToPortfolio,
  addStockToPortfolioById,
  setHoldingToReplace,
} from '@/redux/modules/edit-portfolio';
import { addSearchItemLog } from '@/redux/modules/activity-log';
import { loadQuickAddItems } from '@/redux/modules/advanced-search';
import { loadRecentSearchItems, QUICK_SEARCH_REDUCER_NAME, setFocusedQuickSearchEditPorfolio } from '@/redux/modules/quick-search';

// Components
import PortfolioScreenerModal from '@/views/components/modals/PortfolioScreenerModal';
import { QuickSearchV2 } from '@/views/components/search/quick-search-v2';
import { withErrorBoundary } from '@/views/components/errors';

// Types
import AdvancedSearchItem from '@/types/advanced-search-item';
import Fund from '@/types/fund';
import Portfolio from '@/types/portfolio';
import PortfolioHolding from '@/types/portfolio-holding';
import QuickAddItems from '@/types/quick-add-items';
import RecentSearchItem from '@/types/recent-search-item';
import { ReduxState } from '@/redux/modules/types';

// Utils
import notification from '@/utils/notification';
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { SEARCH_ITEM_DEST, SEARCH_ITEM_SOURCE } from '@/utils/activity-log';
import { showError } from '@/utils/errors';

// Legacy support
import LegacyQuickSearchV2 from '@/views/components/search/quick-search-v2/LegacyQuickSearchV2';
import withLegacyVersion from '@/views/components/hocs/withLegacyVersion';

type DispatchProps = {
  addAltToPortfolioById: (id: string, holdingToReplace?: PortfolioHolding, showReplacedFirst?: boolean) => Promise<void>;
  addFundToPortfolioById: (id: string, holdingToReplace?: PortfolioHolding, showReplacedFirst?: boolean) => Promise<void>;
  addFundsToPortfolio: (funds: Fund[]) => Promise<void[]>;
  addSearchItemLog: (id: string, type: string, source: string, dest: string) => void;
  addStockToPortfolioById: (id: string, holdingToReplace?: PortfolioHolding, showReplacedFirst?: boolean) => Promise<void>;
  loadQuickAddItems: () => Promise<void>;
  loadRecentSearchItems: (reducerName: string) => Promise<void>;
  setFocusedQuickSearchEditPorfolio: (focused?: boolean) => void;
  setHoldingToReplace: (holdingToReplace?: PortfolioHolding) => void;
};

type StateProps = {
  holdingToReplace?: PortfolioHolding;
  isFocusedQuickSearch?: boolean;
  portfolio: Portfolio;
  quickAddItems: QuickAddItems[];
  recentSearchItems: RecentSearchItem[];
  portfolioHasMissingHoldings?: boolean;
};

type Props = DispatchProps & StateProps;

const PortfolioEditorQuickSearch = ({
  addAltToPortfolioById,
  addFundToPortfolioById,
  addFundsToPortfolio,
  addSearchItemLog,
  addStockToPortfolioById,
  holdingToReplace,
  isFocusedQuickSearch,
  loadQuickAddItems,
  loadRecentSearchItems,
  portfolio,
  quickAddItems,
  setFocusedQuickSearchEditPorfolio,
  setHoldingToReplace,
  portfolioHasMissingHoldings = false,
}: Props) => {
  useEffect(() => {
    loadQuickAddItems();

    // TODO: implement and test the recent searches from CONN-6033
    // loadRecentSearchItems(QUICK_SEARCH_REDUCER_NAME.EDIT_PORTFOLIO);
  }, [loadQuickAddItems, loadRecentSearchItems]);

  const [showScreenerModal, setShowScreenerModal] = useState<boolean>(false);
  const shouldDisableQuickSearch = portfolioHasMissingHoldings && !isFocusedQuickSearch;

  const handleOnAddItemById = (fund: Fund) => {
    addFundToPortfolioById(fund._id, holdingToReplace, true)
      .then(() => {
        addSearchItemLog(fund._id, COMPARISON_ITEM_TYPES.FUND, SEARCH_ITEM_SOURCE.QUICKSEARCH, SEARCH_ITEM_DEST.PORTFOLIO);
        return handleOnCloseScreenerModal();
      })
      .catch(showError);
  };

  const handleOnAddItems = (funds: AdvancedSearchItem[]) => {
    addFundsToPortfolio(funds as Fund[])
      .then(() => handleOnCloseScreenerModal())
      .catch(err => notification.errorFromKey(err.errorKey));
  };

  const handleOnCloseScreenerModal = () => {
    if (holdingToReplace) {
      setHoldingToReplace(undefined);
    }

    if (isFocusedQuickSearch) {
      setFocusedQuickSearchEditPorfolio(false);
    }

    setShowScreenerModal(false);
  };

  return (
    <>
      <QuickSearchV2
        holdingToReplace={holdingToReplace}
        onAddAltById={addAltToPortfolioById}
        onAddBenchmarkById={addFundToPortfolioById}
        onAddFundById={addFundToPortfolioById}
        onAddStockById={addStockToPortfolioById}
        onOpenScreenerModal={setShowScreenerModal}
        onSetFocusedItem={setFocusedQuickSearchEditPorfolio}
        portfolio={portfolio}
        quickAddItems={quickAddItems}
        targetReducer={QUICK_SEARCH_REDUCER_NAME.EDIT_PORTFOLIO}
        disabled={shouldDisableQuickSearch}
      />

      {showScreenerModal && (
        <PortfolioScreenerModal onAddItem={handleOnAddItemById} onAddItems={handleOnAddItems} onClose={handleOnCloseScreenerModal} show={showScreenerModal} />
      )}
    </>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  holdingToReplace: state.editPortfolio.holdingToReplace,
  isFocusedQuickSearch: state.quickSearchEditPortfolio.focused,
  portfolio: state.editPortfolio.portfolio,
  quickAddItems: state.advancedSearch.quickAddItems,
  recentSearchItems: state.quickSearchEditPortfolio.recentSearchItems,
});

const mapDispatchToProps = {
  addAltToPortfolioById,
  addFundToPortfolioById,
  addFundsToPortfolio,
  addSearchItemLog,
  addStockToPortfolioById,
  loadQuickAddItems,
  loadRecentSearchItems,
  setFocusedQuickSearchEditPorfolio,
  setHoldingToReplace,
};

export default withLegacyVersion(connect(mapStateToProps, mapDispatchToProps)(withErrorBoundary(PortfolioEditorQuickSearch)), LegacyQuickSearchV2);
